import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  TrendingUp, TrendingDown, Minus, FileText, Calendar,
  AlertTriangle, CheckCircle2, BarChart3, Loader2, Brain,
  Activity, Droplets, Heart, Beaker
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

const METRIC_ICONS = {
  'Blood Sugar': Droplets,
  'Cholesterol': Heart,
  'Hemoglobin': Activity,
  'default': Beaker
};

const LabComparator = ({ user, setUser }) => {
  const { t } = useTranslation();
  const [reports, setReports] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/compare`);
      setReports(response.data.reports || []);
    } catch (error) {
      console.error('Error loading reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzeTrends = async () => {
    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/reports/analyze-trends`);
      setAnalysis(response.data);
      toast.success('Trend analysis completed!');
    } catch (error) {
      toast.error('Failed to analyze trends');
    } finally {
      setAnalyzing(false);
    }
  };

  const getTrendIcon = (trend) => {
    switch(trend?.toLowerCase()) {
      case 'improving': return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'worsening': return <TrendingDown className="w-4 h-4 text-red-500" />;
      default: return <Minus className="w-4 h-4 text-yellow-500" />;
    }
  };

  const getTrendColor = (trend) => {
    switch(trend?.toLowerCase()) {
      case 'improving': return 'bg-green-100 text-green-700';
      case 'worsening': return 'bg-red-100 text-red-700';
      default: return 'bg-yellow-100 text-yellow-700';
    }
  };

  // Mock chart data based on reports
  const getChartData = () => {
    return reports.slice(0, 6).reverse().map((report, idx) => ({
      date: new Date(report.date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' }),
      bloodSugar: 90 + Math.random() * 40,
      cholesterol: 150 + Math.random() * 50,
      hemoglobin: 12 + Math.random() * 3
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
        <Navigation user={user} setUser={setUser} />
        <div className="flex items-center justify-center h-[80vh]">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      </div>
    );
  }

  const chartData = getChartData();

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      <Navigation user={user} setUser={setUser} />
      
      <div className="container mx-auto px-4 py-6 pt-24">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-purple-600 to-blue-600">
              <BarChart3 className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-800">Lab Report Comparator</h1>
              <p className="text-gray-600">Track and analyze your health metrics over time</p>
            </div>
          </div>
          
          <Button 
            onClick={handleAnalyzeTrends}
            disabled={analyzing || reports.length === 0}
            className="bg-gradient-to-r from-purple-600 to-blue-600"
          >
            {analyzing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Brain className="w-4 h-4 mr-2" />}
            AI Trend Analysis
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <FileText className="w-8 h-8 text-purple-500" />
                <div>
                  <p className="text-2xl font-bold">{reports.length}</p>
                  <p className="text-sm text-gray-500">Total Reports</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Calendar className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="text-2xl font-bold">
                    {reports.length > 0 ? new Date(reports[0]?.date).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : 'N/A'}
                  </p>
                  <p className="text-sm text-gray-500">Latest Report</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-8 h-8 text-green-500" />
                <div>
                  <p className="text-2xl font-bold">{analysis?.trends?.filter(t => t.trend === 'improving').length || 0}</p>
                  <p className="text-sm text-gray-500">Improving Metrics</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 text-red-500" />
                <div>
                  <p className="text-2xl font-bold">{analysis?.alerts?.length || 0}</p>
                  <p className="text-sm text-gray-500">Health Alerts</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {reports.length === 0 ? (
          <Card className="p-8 text-center">
            <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Lab Reports Yet</h3>
            <p className="text-gray-500 mb-4">Upload your lab reports to see trend analysis</p>
            <Button onClick={() => window.location.href = '/reports'}>
              Upload Reports
            </Button>
          </Card>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="trends">Trend Charts</TabsTrigger>
              <TabsTrigger value="analysis">AI Analysis</TabsTrigger>
              <TabsTrigger value="reports">All Reports</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Health Metrics Overview</CardTitle>
                  <CardDescription>Your key health indicators at a glance</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="bloodSugar" name="Blood Sugar (mg/dL)" stroke="#8b5cf6" strokeWidth={2} />
                        <Line type="monotone" dataKey="hemoglobin" name="Hemoglobin (g/dL)" stroke="#ef4444" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Trends */}
              {analysis?.trends && analysis.trends.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {analysis.trends.slice(0, 3).map((trend, idx) => (
                    <Card key={`item-${idx}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium">{trend.metric}</span>
                          {getTrendIcon(trend.trend)}
                        </div>
                        <Badge className={getTrendColor(trend.trend)}>
                          {trend.trend}
                        </Badge>
                        <p className="text-sm text-gray-500 mt-2">{trend.details}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Trends Tab */}
            <TabsContent value="trends" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Droplets className="w-5 h-5 text-purple-500" />
                      Blood Sugar Trend
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[250px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis domain={[70, 150]} />
                          <Tooltip />
                          <Area type="monotone" dataKey="bloodSugar" stroke="#8b5cf6" fill="#8b5cf680" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Heart className="w-5 h-5 text-red-500" />
                      Cholesterol Trend
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[250px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis domain={[100, 250]} />
                          <Tooltip />
                          <Area type="monotone" dataKey="cholesterol" stroke="#ef4444" fill="#ef444480" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Activity className="w-5 h-5 text-green-500" />
                      Hemoglobin Trend
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[250px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis domain={[10, 18]} />
                          <Tooltip />
                          <Area type="monotone" dataKey="hemoglobin" stroke="#22c55e" fill="#22c55e80" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* AI Analysis Tab */}
            <TabsContent value="analysis" className="space-y-6">
              {!analysis ? (
                <Card className="p-8 text-center">
                  <Brain className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-xl font-semibold mb-2">AI Analysis Not Generated</h3>
                  <p className="text-gray-500 mb-4">Click the button above to generate AI-powered trend analysis</p>
                  <Button onClick={handleAnalyzeTrends} disabled={analyzing}>
                    {analyzing ? 'Analyzing...' : 'Generate Analysis'}
                  </Button>
                </Card>
              ) : (
                <>
                  {/* Summary */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Analysis Summary</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-700">{analysis.analysis}</p>
                      <p className="text-sm text-gray-500 mt-2">Based on {analysis.reports_analyzed} reports</p>
                    </CardContent>
                  </Card>

                  {/* Trends Grid */}
                  {analysis.trends && analysis.trends.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Metric Trends</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {analysis.trends.map((trend, idx) => {
                            const Icon = METRIC_ICONS[trend.metric] || METRIC_ICONS.default;
                            return (
                              <div key={`item-${idx}`} className="p-4 bg-gray-50 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <Icon className="w-5 h-5 text-gray-600" />
                                    <span className="font-medium">{trend.metric}</span>
                                  </div>
                                  <Badge className={getTrendColor(trend.trend)}>
                                    {getTrendIcon(trend.trend)}
                                    <span className="ml-1">{trend.trend}</span>
                                  </Badge>
                                </div>
                                <p className="text-sm text-gray-600">{trend.details}</p>
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Alerts */}
                  {analysis.alerts && analysis.alerts.length > 0 && (
                    <Card className="border-red-200">
                      <CardHeader>
                        <CardTitle className="text-red-600 flex items-center gap-2">
                          <AlertTriangle className="w-5 h-5" />
                          Health Alerts
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-2">
                          {analysis.alerts.map((alert, idx) => (
                            <li key={`item-${idx}`} className="flex items-start gap-2 text-red-700">
                              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                              {alert}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}

                  {/* Recommendations */}
                  {analysis.recommendations && analysis.recommendations.length > 0 && (
                    <Card className="border-green-200">
                      <CardHeader>
                        <CardTitle className="text-green-600 flex items-center gap-2">
                          <CheckCircle2 className="w-5 h-5" />
                          Recommendations
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-2">
                          {analysis.recommendations.map((rec, idx) => (
                            <li key={`item-${idx}`} className="flex items-start gap-2 text-green-700">
                              <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                              {rec}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}
            </TabsContent>

            {/* Reports List Tab */}
            <TabsContent value="reports" className="space-y-4">
              {reports.map((report, idx) => (
                <Card key={report.id || idx} className="hover:shadow-lg transition-all">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <FileText className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium">{report.report_name}</p>
                          <p className="text-sm text-gray-500">{report.report_type}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant="outline">{new Date(report.date).toLocaleDateString()}</Badge>
                        {report.analysis && (
                          <p className="text-xs text-green-600 mt-1">✓ Analyzed</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
};

export default LabComparator;